import type { Flight } from '../types';
import { getCoords, getCountry, greatCircleDistance, estimateFlightHours } from './airportCoords';

export type Temporal = 'past' | 'future' | 'both';

export interface TemporalRoute {
  from: string;
  to: string;
  count: number;
  temporal: Temporal;
}

export interface TemporalAirport {
  iata: string;
  temporal: Temporal;
}

export interface FlightStatsResult {
  totalFlights: number;
  totalNauticalMiles: number;
  totalEstimatedHours: number;
  uniqueAirports: string[];
  uniqueRoutes: { from: string; to: string; count: number }[];
  temporalRoutes: TemporalRoute[];
  temporalAirports: TemporalAirport[];
  topAirports: { iata: string; count: number }[];
  topAirlines: { code: string; count: number }[];
  topAircraft: { type: string; count: number }[];
  countriesVisited: number;
  countryCodes: string[];
}

export function computeFlightStats(flights: Flight[], baseAirport?: string, today?: string): FlightStatsResult {
  const todayStr = today ?? new Date().toISOString().slice(0, 10);
  const airportCounts = new Map<string, number>();
  const routeCounts = new Map<string, { from: string; to: string; count: number }>();
  const airlineCounts = new Map<string, number>();
  const aircraftCounts = new Map<string, number>();
  const countries = new Set<string>();
  let totalNm = 0;

  const allAirports = new Set<string>();

  // Temporal tracking
  const routeTemporal = new Map<string, { past: boolean; future: boolean }>();
  const airportTemporal = new Map<string, { past: boolean; future: boolean }>();

  // Only count past flights for stats
  const pastFlights = flights.filter((f) => f.date <= todayStr);

  for (const f of flights) {
    const isPast = f.date <= todayStr;

    // Track all unique airports (for globe dots)
    allAirports.add(f.departureAirport);
    allAirports.add(f.arrivalAirport);

    // Temporal: airports
    for (const iata of [f.departureAirport, f.arrivalAirport]) {
      const t = airportTemporal.get(iata) ?? { past: false, future: false };
      if (isPast) t.past = true; else t.future = true;
      airportTemporal.set(iata, t);
    }

    // Route counts (normalized: alphabetically smaller first) — all flights for globe
    const [a, b] = [f.departureAirport, f.arrivalAirport].sort();
    const routeKey = `${a}-${b}`;
    const existing = routeCounts.get(routeKey);
    if (existing) {
      existing.count++;
    } else {
      routeCounts.set(routeKey, { from: a, to: b, count: 1 });
    }

    // Temporal: routes
    const rt = routeTemporal.get(routeKey) ?? { past: false, future: false };
    if (isPast) rt.past = true; else rt.future = true;
    routeTemporal.set(routeKey, rt);
  }

  // Stats only from past flights
  for (const f of pastFlights) {
    airportCounts.set(f.arrivalAirport, (airportCounts.get(f.arrivalAirport) ?? 0) + 1);

    if (f.airlineCode) {
      airlineCounts.set(f.airlineCode, (airlineCounts.get(f.airlineCode) ?? 0) + 1);
    }
    if (f.aircraftType) {
      aircraftCounts.set(f.aircraftType, (aircraftCounts.get(f.aircraftType) ?? 0) + 1);
    }

    const fromCoord = getCoords(f.departureAirport);
    const toCoord = getCoords(f.arrivalAirport);
    if (fromCoord && toCoord) {
      totalNm += greatCircleDistance(fromCoord, toCoord);
    }

    const depCountry = getCountry(f.departureAirport);
    const arrCountry = getCountry(f.arrivalAirport);
    if (depCountry) countries.add(depCountry);
    if (arrCountry) countries.add(arrCountry);
  }

  const uniqueAirports = [...allAirports];
  const uniqueRoutes = [...routeCounts.values()].sort((a, b) => b.count - a.count);

  const temporalRoutes: TemporalRoute[] = uniqueRoutes.map((r) => {
    const key = [r.from, r.to].sort().join('-');
    const t = routeTemporal.get(key);
    const temporal: Temporal = t?.past && t?.future ? 'both' : t?.future ? 'future' : 'past';
    return { ...r, temporal };
  });

  const temporalAirports: TemporalAirport[] = uniqueAirports.map((iata) => {
    const t = airportTemporal.get(iata);
    const temporal: Temporal = t?.past && t?.future ? 'both' : t?.future ? 'future' : 'past';
    return { iata, temporal };
  });

  const topAirports = [...airportCounts.entries()]
    .filter(([iata]) => iata !== baseAirport)
    .sort((a, b) => b[1] - a[1])
    .map(([iata, count]) => ({ iata, count }));
  const topAirlines = [...airlineCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([code, count]) => ({ code, count }));
  const topAircraft = [...aircraftCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([type, count]) => ({ type, count }));

  return {
    totalFlights: pastFlights.length,
    totalNauticalMiles: Math.round(totalNm),
    totalEstimatedHours: Math.round(estimateFlightHours(totalNm)),
    uniqueAirports,
    uniqueRoutes,
    temporalRoutes,
    temporalAirports,
    topAirports,
    topAirlines,
    topAircraft,
    countriesVisited: countries.size,
    countryCodes: [...countries].sort(),
  };
}
